import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MedicationIcon from "@mui/icons-material/Medication";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { useMutationDeleteCart } from "../../../queries/product/hooks/useMutationDeleteCart";
import { NotificationType } from "../../../constants/global";
import { ToasterContext } from "../Toaster/ToasterContext";
import { CART } from "../../../queries/product/productTypeDefs";
import { UserContext } from "../../../hooks/userContext";
import IconButton from "@mui/material/IconButton";
import RemoveIcon from "@mui/icons-material/Remove";
import { InputBase, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useMutationUpdateCart } from "../../../queries/product/hooks/useMutationUpdateCart";
import axios from "axios";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

interface IShoppingCartItemProps {
  cartItem: any;
}

const SealTag = ({ sealed }: { sealed: boolean }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      px: "8px",
      py: "3px",
      borderRadius: "5px",
      fontFamily: MONO_FONT,
      fontSize: "10px",
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      color: sealed ? "#2E7D32" : "#B45309",
      backgroundColor: sealed ? "#E8F5E9" : "#FEF3C7",
    }}
  >
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        bgcolor: sealed ? "#66BB6A" : "#F59E0B",
        flexShrink: 0,
      }}
    />
    {sealed ? "Sealed" : "Non-Sealed"}
  </Box>
);

export const ShoppingCartItem: React.FunctionComponent<IShoppingCartItemProps> = ({
  cartItem,
}) => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const [packageAvailable, setPackageAvailable] = useState<number>(cartItem.quantity);
  const [deleteCart, { loading }] = useMutationDeleteCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: "asc" },
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const [updateCart, { loading: updateCartLoading }] = useMutationUpdateCart();
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!cartItem) {
      return;
    }
    const ndc = cartItem.item.ndc;
    const formattedNdc = ndc.replace(/-/g, "");
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
    axios
      .get(imageUrlApi)
      .then((response) => {
        setImageUrl(response.data.imageUrl);
      })
      .catch((error) => {
        console.error("Error fetching drug image:", error);
      });
  }, [cartItem]);

  useEffect(() => {
    setPackageAvailable(cartItem.quantity);
  }, [cartItem.quantity]);

  const deleteCartItem = (item: any) => {
    if (!loading) {
      deleteCart({ variables: { where: { id: item.id } } }).catch((error: any) => {
        addToast({ type: NotificationType.Error, title: error.message });
      });
    }
  };

  const handleChangePackageAvailable = (isPlus: boolean = true) => {
    const requestData = {
      User: {
        connect: {
          id: +id,
        },
      },
      Product: {
        connect: {
          id: cartItem.item.id,
        },
      },
    };

    if (isPlus) {
      updateCart({
        variables: {
          data: { ...requestData, quantity: { increment: 1 } },
          where: { id: cartItem.id },
        },
      })
        .then((res: any) => {
          setPackageAvailable(res.data.updateCart.quantity);
        })
        .catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message });
        });
    } else {
      updateCart({
        variables: {
          data: { ...requestData, quantity: { decrement: 1 } },
          where: { id: cartItem.id },
        },
      })
        .then((res: any) => {
          setPackageAvailable(res.data.updateCart.quantity);
        })
        .catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message });
        });
    }
  };

  const getPrice = () => {
    const price = Number(cartItem.item.price.replace(/[^0-9.-]+/g, ""));
    return price * packageAvailable;
  };

  const isSealed = cartItem.item.packageCondition === 1;
  const ndc = cartItem.item.ndc;
  const strength = cartItem.item.strength;

  const thumbSx = {
    width: 48,
    height: 48,
    borderRadius: "8px",
    flexShrink: 0,
  };

  const qtyControl = (
    <Box
      sx={{
        p: "2px 4px",
        display: "flex",
        alignItems: "center",
        width: 100,
        height: 32,
        border: `1px solid ${LINE}`,
        borderRadius: "8px",
        bgcolor: "#FFFFFF",
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <IconButton
          size="small"
          sx={{ p: 0, color: PRIMARY }}
          onClick={() => handleChangePackageAvailable(false)}
          disabled={packageAvailable === 1 || updateCartLoading}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Divider sx={{ height: 24, m: 0.5, borderColor: LINE_SOFT }} orientation="vertical" />
        <InputBase
          sx={{
            flex: 1,
            minWidth: 0,
            input: {
              textAlign: "center",
              padding: "2px 0",
              fontWeight: 600,
              fontFamily: BODY_FONT,
            },
          }}
          value={packageAvailable}
          inputProps={{ readOnly: true, "aria-label": "Quantity" }}
        />
        <Divider sx={{ height: 24, m: 0.5, borderColor: LINE_SOFT }} orientation="vertical" />
        <IconButton
          size="small"
          sx={{ p: 0, color: PRIMARY }}
          onClick={() => handleChangePackageAvailable()}
          disabled={packageAvailable === cartItem.item.quantity || updateCartLoading}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        mx: 2,
        my: 1.5,
        p: 2,
        bgcolor: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: "10px",
        fontFamily: BODY_FONT,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", gap: 1.5 }}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt="Drug"
            sx={{ ...thumbSx, alignSelf: "flex-start", border: `1px solid ${LINE_SOFT}`, objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              ...thumbSx,
              alignSelf: "flex-start",
              bgcolor: "#F1F5F9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MedicationIcon sx={{ fontSize: 24, color: PRIMARY }} />
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                fontWeight: 600,
                color: INK,
                fontSize: 14,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {cartItem.item.drugName}
              {cartItem.item.isFrozen && (
                <Tooltip title="Refrigerated" arrow placement="top">
                  <AcUnitIcon
                    sx={{
                      fontSize: 16,
                      color: "#4A90D9",
                      ml: 0.5,
                      mb: "-3px",
                      verticalAlign: "middle",
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
              <SealTag sealed={isSealed} />
              <IconButton
                aria-label="Remove from cart"
                size="small"
                disabled={loading}
                onClick={() => deleteCartItem(cartItem)}
                sx={{
                  color: INK_SOFT,
                  borderRadius: "8px",
                  "&:hover": { color: "#C62828", bgcolor: "#FFEBEE" },
                }}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: 1.5,
              rowGap: 0.25,
              fontFamily: MONO_FONT,
              fontSize: "11px",
              color: INK_SOFT,
            }}
          >
            {ndc && <Box sx={{ wordBreak: "break-all" }}>{ndc}</Box>}
            {strength && <Box sx={{ wordBreak: "break-word" }}>{strength}</Box>}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mt: "auto",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              {qtyControl}
              <Box sx={{ fontFamily: MONO_FONT, fontSize: "11px", color: INK_SOFT, whiteSpace: "nowrap" }}>
                Available: {cartItem.item.quantity}
              </Box>
            </Box>
            <Box
              sx={{
                fontFamily: DISPLAY_FONT,
                fontWeight: 700,
                fontSize: 16,
                color: INK,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              ${getPrice().toFixed(2)}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ShoppingCartItem;
